import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { FORUM_COMMENT_ID_PREFIX } from '../../../../config/constants';
import useToast from '../../../../hooks/useToast';
import useTypedDispatch from '../../../../hooks/useTypedDispatch';
import { commentEdit } from '../../../../store/actions/Entity/Comment';
import type { FormForumCommentValues } from '../../../Forms/Forum/Comment';
import ForumPost, { type ForumPostProps } from '../../../Forum/Post';
import useRefreshTrigger from '../../hooks/useRefreshTrigger';
import type { SearchForumThreadHit } from '../../types';

export interface SearchForumThreadResultsHitProps {
  hit: SearchForumThreadHit;
  originalPosterId: number;
  onClickReply: ForumPostProps['onClickReply'];
}

const SearchForumThreadResultsHit: React.FC<SearchForumThreadResultsHitProps> = (props) => {
  const {
    hit,
    originalPosterId,
    onClickReply,
  } = props;

  const commentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const toast = useToast();

  const currentUserId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));
  const nickname = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', currentUserId, 'field_nickname', 0, 'value'], ''));
  const hash = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['router', 'location', 'hash'], ''));

  const { triggerSearchRefresh } = useRefreshTrigger();

  const cid = hit.comment_id[0];
  const created = hit.comment_created[0];
  const changed = hit.comment_changed[0];
  const message = hit.comment_message?.[0];
  const authorId = hit.comment_author_id[0];
  const authorNickname = hit.comment_author_nickname?.[0] || t('pages.forum.anonymous');
  const authorAvatar = hit.comment_author_avatar?.[0] || 1;
  const parentCommentId = hit.parent_comment_id?.[0];
  const parentCommentLink = hit.parent_comment_link?.[0];
  const parentAuthorNickname = hit.parent_comment_author_nickname?.[0];
  const postId = hit.comment_post_id[0];
  const likeCount = hit.comment_like_count[0];
  const isLiked = hit.comment_like_uids?.includes(currentUserId);
  const isInappropriate = hit.comment_inappropriate_uids?.includes(currentUserId);

  const handleEditComment = useCallback(async (values: FormForumCommentValues) => {
    const comment = {
      entity_id: [{ target_id: postId }],
      entity_type: [{ value: 'node' }],
      comment_type: [{ target_id: 'comment_forum' }],
      field_name: [{ value: 'comment_forum' }],
      field_body: [{ value: values.body }],
    };

    try {
      // Wait for the comment to be added.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      await dispatch(commentEdit(cid, comment));

      // Show success toast.
      toast.show(t('pages.forum_comment_edit.success_message'), 'success');

      triggerSearchRefresh();
    } catch (error) {
      if (error) {
        toast.show(t('pages.forum_comment_edit.error_message'), 'error');
      }
    }
  }, [cid, dispatch, postId, t, toast, triggerSearchRefresh]);

  // Focus and scroll to mentioned comment
  // if it matches the hash from the url.
  useEffect(() => {
    if (!commentRef.current) {
      return;
    }

    if (!hash || !hash.startsWith(`#${FORUM_COMMENT_ID_PREFIX}`)) {
      return;
    }

    const id = hash.slice(`#${FORUM_COMMENT_ID_PREFIX}`.length);

    if (id !== String(cid)) {
      return;
    }

    const element = commentRef.current;
    element.focus();
    element.scrollIntoView();
  // We only want to check for this on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ForumPost
      ref={commentRef}
      id={cid}
      type="comment"
      author={authorNickname}
      avatar={authorAvatar}
      publicationDate={new Date(created * 1000)}
      updatedDate={new Date(changed * 1000)}
      message={message}
      messageProcessed={message ? <span className="ForumPost__plain-text">{message}</span> : undefined}
      mentionId={parentCommentId}
      mentionName={parentAuthorNickname}
      mentionLink={parentCommentLink}
      isLiked={isLiked}
      likeCount={likeCount}
      isInappropriate={isInappropriate}
      isMyPost={currentUserId === authorId}
      isOriginalPoster={authorId === originalPosterId}
      hasCreatedNickname={!!nickname}
      isFirstPost={false}
      onClickReply={onClickReply}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onEditPost={handleEditComment}
    />
  );
};

export default SearchForumThreadResultsHit;
